'use client';

import { Paper } from '@mui/material';
import { usePathname } from 'next/navigation';

import { ativoPath, parseAtivoResourcePath, type AtivoAvailability, type AtivoCaixaSubview } from '@/libs/ativo-route';
import { TickerCashTabClient, type CashTabViewModel } from './TickerCashTabClient';

/**
 * Client wrapper for the FII cash tab that uses the canonical
 * /ativo/{ticker}/caixa/{subview} URL segments to drive the active
 * sub-view. Falls back to local state when the view model is null
 * (empty base) so the empty state still renders without a hard error.
 */
export function TickerCashTabResource({
  tickerLabel,
  availability,
  viewModel,
}: {
  tickerLabel: string;
  availability: AtivoAvailability;
  viewModel: CashTabViewModel | null;
}) {
  const pathname = usePathname() ?? '';
  const segments = pathname.split('/').filter(Boolean);
  const parsed = parseAtivoResourcePath({
    segments: [tickerLabel, ...segments.slice(2)],
    availability,
  });
  if (!viewModel) {
    return (
      <Paper variant='outlined' sx={{ mt: 2, p: 2, borderStyle: 'dashed', color: 'text.secondary' }}>
        Sem base mensal de ativo/passivo para análise de caixa de {tickerLabel}.
      </Paper>
    );
  }
  return (
    <TickerCashTabClient
      tickerLabel={tickerLabel}
      data={viewModel}
      activeSubview={parsed.caixaSubview}
      buildHref={(subview: AtivoCaixaSubview) =>
        ativoPath.withActiveCaixaSubview({
          ticker: tickerLabel,
          tab: 'caixa',
          caixaSubview: subview,
        })
      }
    />
  );
}
